import { useState } from "react";
import { SellingMockupPicker, type SellingMockupInfo } from "../components/mockup/SellingMockupPicker";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ListingStatsCompare, type StatsSnap } from "../components/analysis/ListingStatsCompare";
import { DesignSetPanel, type DesignSet } from "../components/analysis/DesignSetPanel";
import { FrontBackPanel } from "../components/analysis/FrontBackPanel";
import { CopyrightFixPanel, type CopyrightFix } from "../components/analysis/CopyrightFixPanel";
import { NicheExpansionPanel } from "../components/analysis/NicheExpansionPanel";
import { CopyrightResearchPanel, type UsptoCheck, type DeepResearch } from "../components/analysis/CopyrightResearchPanel";
import {
  ArrowLeft,
  ExternalLink,
  ShoppingBag,
  Tag,
  Star,
  FileText,
  Lightbulb,
  ShieldAlert,
  Palette,
  Copy,
  Check,
  RefreshCw,
  BarChart3,
  Shirt,
} from "lucide-react";
import { apiClient } from "../lib/api";
import { RiskNotes } from "../components/analysis/RiskNotes";
import { SalesEvidence } from "../components/analysis/SalesEvidence";
import { IdeaBriefs } from "../components/analysis/IdeaBriefs";
import { DesignWorkshop } from "../components/analysis/DesignWorkshop";
import { PersonalizationFieldsEditor } from "../components/analysis/PersonalizationFieldsEditor";
import { OrderDesignModal } from "../components/analysis/OrderDesignModal";
import { CustomizationPanel, type CustomizationMap } from "../components/analysis/CustomizationPanel";
import { buildBriefUserPrompt } from "../lib/designPrompt";
import type { Analysis, IdeaBrief, RiskNote, PersonalizationField, ErankListing } from "@erc/shared";
import { familyVariants } from "@erc/shared";
import { customApi } from "../lib/customListing";
import { useEffect } from "react";

// Analizdeki Etsy API ozeti + eklentinin hesapladigi potansiyel (arama karti rozetiyle ayni)
type EtsyStats = { views?: number | null; favorites?: number; ageDays?: number; viewsPerDay?: number | null; favPerDay?: number; potential?: number };

// Cift dilli alan ({en,tr}) veya eski string'i {en,tr}'ye indir
function bi(v: unknown): { en: string; tr: string } {
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    return { en: String(o.en ?? "").trim(), tr: String(o.tr ?? o.en ?? "").trim() };
  }
  const s = String(v ?? "").trim();
  return { en: s, tr: s };
}

export function usptoSearchUrl(term: string): string {
  return `https://tmsearch.uspto.gov/search/search-information?query=${encodeURIComponent(term)}`;
}

const COPYRIGHT_BADGE: Record<string, { label: string; cls: string }> = {
  low: { label: "Düşük Risk", cls: "bg-green-100 text-green-700" },
  medium: { label: "Orta Risk", cls: "bg-amber-100 text-amber-700" },
  high: { label: "Yüksek Risk", cls: "bg-red-100 text-red-700" },
};

export default function AnalysisDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [briefPreset, setBriefPreset] = useState<{ prompt: string; nonce: number }>({ prompt: "", nonce: 0 });
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyText = (field: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    }).catch(() => { /* ignore */ });
  };

  // Atanan satan mockup — analiz yuklenince doldurulur, degistirilince guncellenir
  const [sellingMockup, setSellingMockup] = useState<SellingMockupInfo | null>(null);

  const { data: analysis, isLoading, error } = useQuery({
    queryKey: ["analysis", id],
    queryFn: () => apiClient.getAnalysis(id!),
    select: (data) => data as Analysis,
    enabled: !!id,
    // Eklentiden gelen custom analizde kisisellestirme haritasi ARKA PLANDA
    // cikariliyor; sayfa erken acildiysa hazir olana kadar yokla (yeni analiz
    // baslatmaz). Yalnizca yeni analizlerde, en fazla ~3 dk.
    refetchInterval: (q) => {
      const a = q.state.data as (Analysis & { customization?: unknown; createdAt?: string }) | undefined;
      if (!a || a.customization) return false;
      const tags = a.source?.tags ?? [];
      const custom = tags.some((t) => String(t).toLowerCase() === "custom");
      const fresh = a.createdAt ? Date.now() - new Date(a.createdAt).getTime() < 3 * 60_000 : false;
      return custom && fresh ? 4000 : false;
    },
  });

  useEffect(() => {
    const m = (analysis as unknown as { sellingMockup?: SellingMockupInfo | null })?.sellingMockup;
    if (m !== undefined) setSellingMockup(m ?? null);
  }, [analysis]);

  const queryClient = useQueryClient();
  const customToggle = useMutation({
    mutationFn: (custom: boolean) => apiClient.setAnalysisCustom(id!, custom),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["analysis", id] }),
  });

  const regenerateMutation = useMutation({
    mutationFn: async () => apiClient.regenerateAnalysisListing(id!),
    onSuccess: (updated) => {
      queryClient.setQueryData(["analysis", id], updated);
    },
  });

  const createDraftMutation = useMutation({
    mutationFn: async (_brief: IdeaBrief) => {
      return apiClient.createListingDraft({
        analysisId: id!,
        title: analysis!.suggestedTitle,
        description: analysis!.suggestedDescription,
        tags: analysis!.suggestedTags,
      });
    },
    onSuccess: (draft) => {
      navigate(`/listing-builder?id=${draft.id}`);
    },
  });

  // Custom Liste Studyosu: analizin kaydini acar (yoksa olusturur)
  const openStudio = useMutation({
    mutationFn: () => customApi<{ id: string }>("POST", "", { analysisId: id }),
    onSuccess: (r) => navigate(`/custom/${r.id}`),
    onError: (e) => alert((e as Error).message),
  });

  // Studyodan "#custom-tasarim" ile gelinince panele kaydir (panel veri gelince cizilir)
  useEffect(() => {
    if (!analysis || window.location.hash !== "#custom-tasarim") return;
    const t = setTimeout(() => document.getElementById("custom-tasarim")?.scrollIntoView({ behavior: "smooth" }), 150);
    return () => clearTimeout(t);
  }, [analysis?.id]);

  // Kisisellestirme alanlari (duzenlenebilir)
  const [persoFields, setPersoFields] = useState<PersonalizationField[]>([]);
  const [persoDirty, setPersoDirty] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  useEffect(() => {
    const p = (analysis?.source?.rawData?.personalization as PersonalizationField[]) || [];
    setPersoFields(p);
    setPersoDirty(false);
  }, [analysis?.id]);
  const savePersoMutation = useMutation({
    mutationFn: () => apiClient.setAnalysisPersonalization(id!, persoFields),
    onSuccess: () => { setPersoDirty(false); queryClient.invalidateQueries({ queryKey: ["analysis", id] }); },
  });

  // eRank verisi: bu urunun listing ID'sinden (yuklenmis CSV varsa) metrikleri getir
  const erankListingId = (analysis?.source?.url ?? "").match(/listing\/(\d+)/)?.[1] ?? null;
  const { data: erank } = useQuery({
    queryKey: ["erank-by-listing", erankListingId],
    queryFn: () => apiClient.getErankByListing(erankListingId!),
    enabled: !!erankListingId,
  });

  // Kuyruk (arama karti) uzerinden analiz edilen custom urunler personalization
  // panelini gormedigi icin bu alanlar bos kalir. Bu buton urunu yeni sekmede
  // #erc-perso=<id> ile acar; eklenti canli DOM'dan kazir, API'ye kaydeder.
  // Sonrasinda birkaç kez yeniden cekerek guncel veriyi gosteririz.
  const fetchPersoFromEtsy = () => {
    const url = analysis?.source?.url;
    if (!url) return;
    const base = url.split("#")[0];
    window.open(`${base}#erc-perso=${id}`, "_blank");
    let tries = 0;
    const iv = setInterval(() => {
      tries++;
      queryClient.invalidateQueries({ queryKey: ["analysis", id] });
      if (tries >= 8) clearInterval(iv);
    }, 3000);
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="space-y-4 animate-pulse">
          <div className="h-8 w-64 rounded bg-gray-200" />
          <div className="h-4 w-48 rounded bg-gray-200" />
          <div className="mt-6 grid grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 rounded-xl bg-gray-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <p className="text-gray-500">Analiz bulunamadı.</p>
        <Link to="/dashboard" className="mt-3 text-sm text-indigo-600 hover:underline">
          Dashboard'a dön
        </Link>
      </div>
    );
  }

  const riskNotes = (analysis.riskNotes ?? []) as RiskNote[];
  const perso = (analysis.source?.rawData?.personalization as Array<{ label: string; type: string; required?: boolean; instructions?: string; options?: string[]; maxChar?: number; maxFiles?: number }>) || [];
  // "Custom degil" isareti: satici kisisellestirmeyi yalniz alici notu icin acmis
  const notCustom = (analysis.source?.rawData as { notCustom?: boolean } | null)?.notCustom === true;
  const isCustom = !notCustom && ((analysis.source?.tags ?? []).some((t) => String(t).toLowerCase() === "custom") || perso.length > 0);
  const keywordClusters = analysis.keywordClusters as {
    primary: string[];
    secondary: string[];
    longTail: string[];
    styleDescriptors: string[];
  };

  return (
    <div className="p-8">
      {/* Back button */}
      <Link
        to="/dashboard"
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Dashboard
      </Link>

      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
              {analysis.productType}
            </span>
            <span className="rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700">
              {analysis.niche}
            </span>
            {analysis.copyright && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${(COPYRIGHT_BADGE[analysis.copyright.level] ?? COPYRIGHT_BADGE.low).cls}`}>
                <ShieldAlert className="h-3 w-3" /> Telif: {(COPYRIGHT_BADGE[analysis.copyright.level] ?? COPYRIGHT_BADGE.low).label}
              </span>
            )}
            {isCustom && (
              <span className="rounded-full bg-pink-100 px-2.5 py-0.5 text-xs font-semibold text-pink-700">Custom</span>
            )}
            {(isCustom || notCustom || perso.length > 0) && (
              <button onClick={() => customToggle.mutate(!isCustom)} disabled={customToggle.isPending}
                title={isCustom ? "Satıcı kişiselleştirmeyi yalnız alıcı notu için açtıysa: liste custom sayılmaz, ilhamın soruları taslağa kopyalanmaz" : "Yeniden custom liste olarak işaretle"}
                className="rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                {customToggle.isPending ? "Kaydediliyor…" : isCustom ? "Custom değil olarak işaretle" : "Custom olarak işaretle"}
              </button>
            )}
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            {analysis.source?.title ?? "Analiz Detayı"}
          </h2>
          {analysis.source?.url && (
            <a
              href={analysis.source.url.split("#")[0]}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Orijinal Etsy Sayfası
            </a>
          )}
        </div>

        <div className="flex items-start gap-3">
          {/* Satan mockup: bu ilham listesinden uretilen listenin ANA gorseli.
              Atanmissa gorunur, atanmamissa buradan secilebilir. */}
          <div className="w-56">
            <SellingMockupPicker
              analysisId={analysis.id}
              value={sellingMockup}
              productTypeKey={(analysis as unknown as { productTypeKey?: string | null }).productTypeKey}
              onChange={setSellingMockup}
            />
          </div>

        {isCustom && (
          <button
            onClick={() => openStudio.mutate()}
            disabled={openStudio.isPending}
            title="Bu custom ürünü ilhamdan Etsy'ye adım adım hazırla"
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <Shirt className="h-4 w-4 text-indigo-600" />
            Custom liste çalışması
          </button>
        )}
        <button
          onClick={() => createDraftMutation.mutate(analysis.ideaBriefs[0])}
          disabled={createDraftMutation.isPending}
          className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60 transition-colors"
        >
          <FileText className="h-4 w-4" />
          Listing Taslağı Oluştur
        </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main column */}
        <div className="col-span-2 space-y-6">
          {/* Ürün fotoğrafı (+ yanında eRank verisi varsa) */}
          {analysis.source?.imageUrls?.[0] && (
            <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row">
                <img
                  src={analysis.source.imageUrls[0]}
                  alt={analysis.source.title ?? ""}
                  className={`max-h-96 rounded-lg object-contain bg-gray-50 ${erank ? "md:w-2/3 w-full" : "w-full"}`}
                  loading="lazy"
                />
                {erank && <ErankPanel e={erank} />}
              </div>
            </section>
          )}
          {analysis.research && <SalesEvidence research={analysis.research} />}

          {/* On + arka tek liste: iki tasarim tek listede (arka alanlara arka tasarim) */}
          <FrontBackPanel analysisId={analysis.id} />

          {/* Nis genisletme: best seller'dan ozgun alt nis / capraz nis fikirleri (Trend Radari'nda dogrulanir) */}
          <NicheExpansionPanel analysisId={analysis.id} />

          {/* Tasarim seti: her tasarimin tisort rengi sabit (alici tasarimi secer) */}
          <DesignSetPanel
            analysisId={analysis.id}
            initial={((analysis as unknown as { designSet?: DesignSet | null }).designSet) ?? null}
            images={analysis.source?.imageUrls ?? []}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ["analysis", id] })}
          />

          {/* Custom liste: ornek isim tespiti -> alternatif isimli tasarim -> kolaj mockup */}
          {isCustom && (
            <div id="custom-tasarim" className="scroll-mt-4">
            <CustomizationPanel
              analysisId={analysis.id}
              initial={((analysis as unknown as { customization?: CustomizationMap | null }).customization) ?? null}
              sourceImages={analysis.source?.imageUrls ?? []}
              productTypeKey={analysis.productTypeKey}
            />
            </div>
          )}

          {/* Kişiselleştirme alanları — düzenlenebilir */}
          {(isCustom || persoFields.length > 0) && (
            <section className="rounded-xl border border-pink-200 bg-pink-50/40 p-4 shadow-sm">
              <div className="mb-1 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-pink-800">Kişiselleştirme Alanları ({persoFields.length})</h3>
                <div className="flex items-center gap-2">
                  {analysis.source?.url && (
                    <button onClick={fetchPersoFromEtsy}
                      title="Ürünü yeni sekmede açar; eklenti kişiselleştirme alanlarını çekip bu analize ekler. Birkaç saniye sonra otomatik yenilenir."
                      className="rounded-lg border border-pink-300 bg-white px-3 py-1 text-xs font-medium text-pink-700 hover:bg-pink-50">
                      Custom verilerini çek
                    </button>
                  )}
                  {persoFields.length > 0 && (
                    <button onClick={() => setShowOrderModal(true)}
                      className="rounded-lg bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700">
                      Sipariş Tasarımı Üret
                    </button>
                  )}
                  {persoDirty && (
                    <button onClick={() => savePersoMutation.mutate()} disabled={savePersoMutation.isPending}
                      className="rounded-lg bg-pink-600 px-3 py-1 text-xs font-medium text-white hover:bg-pink-700 disabled:opacity-50">
                      {savePersoMutation.isPending ? "Kaydediliyor…" : "Kaydet"}
                    </button>
                  )}
                </div>
              </div>
              <p className="mb-3 text-xs text-gray-500">Bu custom ürünün alıcıdan istediği bilgiler. Düzenle → Kaydet. Listing Builder'a gönderilince otomatik oluşur. Yeni sipariş için "Sipariş Tasarımı Üret".</p>
              {persoFields.length === 0 && (
                <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  Kişiselleştirme alanı yok. Bu ürün kuyruktan analiz edildiyse alanlar çekilmemiş olabilir — <b>"Custom verilerini çek"</b> ile ürünü yeni sekmede açıp otomatik doldurabilirsiniz (eklenti kurulu olmalı).
                </p>
              )}
              <PersonalizationFieldsEditor fields={persoFields} onChange={(f) => { setPersoFields(f); setPersoDirty(true); }} />
            </section>
          )}

          {/* Why it sells */}
          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <ShoppingBag className="h-4 w-4 text-indigo-500" />
              <h3 className="text-base font-semibold text-gray-900">
                Neden Satıyor?
              </h3>
            </div>
            <ul className="space-y-2.5">
              {(analysis.whySells?.length ? analysis.whySells : analysis.possibleWhyItSells).map((reason, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-xs font-bold text-green-700">
                    {i + 1}
                  </span>
                  <span>
                    {bi(reason).tr}
                    {bi(reason).en && bi(reason).en !== bi(reason).tr && (
                      <span className="mt-0.5 block text-xs italic text-gray-400">{bi(reason).en}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* Style notes */}
          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <Star className="h-4 w-4 text-indigo-500" />
              <h3 className="text-base font-semibold text-gray-900">Stil Notları</h3>
            </div>
            <p className="prose-etsy">{analysis.styleNotes}</p>
          </section>

          {/* Tasarım Analizi (vision) */}
          {analysis.designAnalysis && (
            <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <Palette className="h-4 w-4 text-fuchsia-500" />
                <h3 className="text-base font-semibold text-gray-900">Tasarım Analizi</h3>
                <span className="rounded-full bg-fuchsia-50 px-2 py-0.5 text-[10px] font-medium text-fuchsia-600">görselden AI</span>
              </div>
              {bi(analysis.designAnalysis.summary).tr && (
                <div className="mb-4">
                  <p className="text-sm leading-relaxed text-gray-800">{bi(analysis.designAnalysis.summary).tr}</p>
                  {bi(analysis.designAnalysis.summary).en && (
                    <p className="mt-1 text-xs italic leading-relaxed text-gray-400">{bi(analysis.designAnalysis.summary).en}</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                {([
                  ["Kompozisyon", analysis.designAnalysis.composition],
                  ["Tema", analysis.designAnalysis.theme],
                  ["Sanat Stili", analysis.designAnalysis.artStyle],
                  ["Tipografi", analysis.designAnalysis.typography],
                  ["Ruh Hali", analysis.designAnalysis.mood],
                ] as [string, unknown][])
                  .filter(([, v]) => bi(v).tr)
                  .map(([label, v]) => (
                    <div key={label}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
                      <p className="mt-0.5 text-sm text-gray-700">{bi(v).tr}</p>
                      {bi(v).en && bi(v).en !== bi(v).tr && (
                        <p className="mt-0.5 text-[11px] italic text-gray-400">{bi(v).en}</p>
                      )}
                    </div>
                  ))}
              </div>
              {analysis.designAnalysis.motifs?.length > 0 && (
                <div className="mt-4">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Motifler</p>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.designAnalysis.motifs.map((m) => (
                      <span key={m} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{m}</span>
                    ))}
                  </div>
                </div>
              )}
              {analysis.designAnalysis.colors?.length > 0 && (
                <div className="mt-4">
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Renk Paleti</p>
                  <div className="flex flex-wrap gap-2">
                    {analysis.designAnalysis.colors.map((c) => (
                      <div key={c} className="flex items-center gap-1.5">
                        <span className="h-6 w-6 rounded-md border border-gray-200" style={{ backgroundColor: c }} />
                        <span className="text-[11px] text-gray-500">{c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Tasarım Atölyesi */}
          <DesignWorkshop analysis={analysis} presetPrompt={briefPreset.prompt} presetNonce={briefPreset.nonce} />

          {/* Keyword clusters */}
          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <Tag className="h-4 w-4 text-indigo-500" />
              <h3 className="text-base font-semibold text-gray-900">Keyword Kümeleri</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(keywordClusters).map(([cluster, keywords]) => (
                <div key={cluster}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {cluster === "primary"
                      ? "Birincil"
                      : cluster === "secondary"
                      ? "İkincil"
                      : cluster === "longTail"
                      ? "Long-tail"
                      : "Stil"}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(keywords as string[]).map((kw) => (
                      <span key={kw} className="tag-pill">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Telif & Risk notları */}
          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-orange-500" />
              <h3 className="text-base font-semibold text-gray-900">Telif Durumu &amp; Risk Notları</h3>
              {analysis.copyright && (
                <span className={`ml-auto rounded-full px-2.5 py-0.5 text-xs font-medium ${(COPYRIGHT_BADGE[analysis.copyright.level] ?? COPYRIGHT_BADGE.low).cls}`}>
                  {(COPYRIGHT_BADGE[analysis.copyright.level] ?? COPYRIGHT_BADGE.low).label}
                </span>
              )}
            </div>
            {analysis.copyright?.summary && (
              <p className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700">{analysis.copyright.summary}</p>
            )}
            <RiskNotes risks={riskNotes} />
            {analysis.copyright && (
              <CopyrightResearchPanel
                analysisId={analysis.id}
                uspto={((analysis.copyright as unknown as { uspto?: UsptoCheck }).uspto) ?? null}
                deep={((analysis.copyright as unknown as { deep?: DeepResearch }).deep) ?? null}
                onChanged={() => queryClient.invalidateQueries({ queryKey: ["analysis", id] })}
              />
            )}
            {analysis.copyright && (
              <CopyrightFixPanel
                analysisId={analysis.id}
                level={analysis.copyright.level}
                fix={((analysis.copyright as unknown as { fix?: CopyrightFix }).fix) ?? null}
                current={{ title: analysis.suggestedTitle, tags: analysis.suggestedTags ?? [] }}
                onChanged={() => queryClient.invalidateQueries({ queryKey: ["analysis", id] })}
              />
            )}
          </section>

          {/* Idea briefs */}
          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-indigo-500" />
              <h3 className="text-base font-semibold text-gray-900">
                Özgün Fikir Brief'leri
              </h3>
            </div>
            <p className="mb-4 text-xs text-gray-500">
              Aşağıdaki brief'ler orijinal ürünün pattern'lerinden ilham alır, ancak
              tamamen özgün bir yönelim önerir.
            </p>
            <IdeaBriefs
              briefs={analysis.ideaBriefs ?? []}
              referenceImageUrl={analysis.source?.imageUrls?.[0]}
              onUseBrief={(brief) => {
                setBriefPreset({ prompt: buildBriefUserPrompt(brief), nonce: Date.now() });
              }}
              onCreateDraft={(brief) => createDraftMutation.mutate(brief)}
            />
          </section>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Suggested listing */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900">
                Önerilen Listing
              </h4>
              <button
                onClick={() => regenerateMutation.mutate()}
                disabled={regenerateMutation.isPending}
                className="flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-[11px] font-medium text-indigo-600 hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Başlık, açıklama ve etiketleri kaynak veriyi kullanarak yeniden üret (görsel analizi tekrar edilmez)"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${regenerateMutation.isPending ? "animate-spin" : ""}`} />
                {regenerateMutation.isPending ? "Üretiliyor..." : "Yeniden Oluştur"}
              </button>
            </div>

            {regenerateMutation.isError && (
              <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-[11px] text-red-600">
                Yeniden üretim başarısız oldu. Tekrar deneyin.
              </p>
            )}

            <div className="space-y-3">
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-xs text-gray-400">Başlık</p>
                  <button onClick={() => copyText("title", analysis.suggestedTitle)} className="flex items-center gap-1 text-[11px] text-indigo-500 hover:text-indigo-700">
                    {copiedField === "title" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {copiedField === "title" ? "Kopyalandı" : "Kopyala"}
                  </button>
                </div>
                <p className="text-sm text-gray-800 leading-snug">
                  {analysis.suggestedTitle}
                </p>
              </div>

              <FamilyVariantsPanel title={analysis.suggestedTitle} sourceTitle={analysis.source?.title} />

              {analysis.suggestedDescription && (
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <p className="text-xs text-gray-400">Açıklama</p>
                    <button onClick={() => copyText("desc", analysis.suggestedDescription)} className="flex items-center gap-1 text-[11px] text-indigo-500 hover:text-indigo-700">
                      {copiedField === "desc" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      {copiedField === "desc" ? "Kopyalandı" : "Kopyala"}
                    </button>
                  </div>
                  <p className="max-h-64 overflow-y-auto whitespace-pre-line rounded-lg bg-gray-50 p-3 text-xs leading-relaxed text-gray-700">
                    {analysis.suggestedDescription}
                  </p>
                </div>
              )}

              <div>
                <p className="text-xs text-gray-400 mb-1">Etiketler ({analysis.suggestedTags.length})</p>
                <div className="flex flex-wrap gap-1">
                  {analysis.suggestedTags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Source info */}
          {analysis.source && (
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h4 className="mb-3 text-sm font-semibold text-gray-900">Kaynak Ürün</h4>
              <div className="space-y-2 text-sm">
                {analysis.source.price && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Fiyat</span>
                    <span className="font-medium">{analysis.source.price}</span>
                  </div>
                )}
                {analysis.source.rating != null && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Rating</span>
                    <span className="font-medium">⭐ {analysis.source.rating}</span>
                  </div>
                )}
                {analysis.source.reviewCount != null && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Yorum</span>
                    <span className="font-medium">
                      {analysis.source.reviewCount.toLocaleString()}
                    </span>
                  </div>
                )}
                {(() => {
                  const d = (analysis.source?.rawData as { demand?: { inCarts?: number; recentPurchases?: number; views24?: number; salesCount?: string; etsyStats?: EtsyStats } } | null)?.demand;
                  if (!d) return null;
                  return (
                    <>
                      {d.recentPurchases != null && (
                        <div className="flex justify-between"><span className="text-gray-500">Son 24 saat</span>
                          <span className="font-semibold text-orange-600">🔥 {d.recentPurchases} kişi aldı</span></div>
                      )}
                      {d.views24 != null && (
                        <div className="flex justify-between"><span className="text-gray-500">Görüntülenme (24 saat)</span>
                          <span className="font-semibold text-amber-700">{d.views24} görüntülenme</span></div>
                      )}
                      {d.inCarts != null && (
                        <div className="flex justify-between"><span className="text-gray-500">Sepette</span>
                          <span className="font-medium text-emerald-700">🛒 {d.inCarts}</span></div>
                      )}
                      {d.etsyStats?.potential != null && (
                        <div className="flex justify-between"><span className="text-gray-500">Potansiyel</span>
                          <span className={`font-semibold ${d.etsyStats.potential >= 70 ? "text-emerald-700" : d.etsyStats.potential >= 45 ? "text-amber-700" : "text-gray-600"}`}>{d.etsyStats.potential} / 100</span></div>
                      )}
                      {/* Goruntulenme / favori / yas: asagidaki karsilastirma tablosunda */}
                      {d.salesCount && (
                        <div className="flex justify-between"><span className="text-gray-500">Toplam satış</span>
                          <span className="font-medium">{d.salesCount}</span></div>
                      )}
                    </>
                  );
                })()}
                {analysis.source.category && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Kategori</span>
                    <span className="font-medium text-right ml-4">
                      {analysis.source.category}
                    </span>
                  </div>
                )}
              </div>

              <ListingStatsCompare
                analysisId={analysis.id}
                capturedAt={String((analysis.source as { createdAt?: string }).createdAt ?? analysis.createdAt)}
                baseline={(analysis.source?.rawData as { demand?: { etsyStats?: EtsyStats } } | null)?.demand?.etsyStats}
                history={((analysis.source?.rawData as { statsHistory?: StatsSnap[] } | null)?.statsHistory) ?? []}
                onRefreshed={() => queryClient.invalidateQueries({ queryKey: ["analysis", id] })}
              />

              {/* Rakip ürünün gerçek Etsy etiketleri (API'den) */}
              {analysis.source.tags && analysis.source.tags.length > 0 && (
                <div className="mt-4 border-t border-gray-100 pt-3">
                  <p className="mb-1 flex items-center gap-1 text-xs text-gray-400">
                    <Tag className="h-3.5 w-3.5 text-emerald-500" /> Rakip Etiketleri ({analysis.source.tags.length}) — Etsy'den
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {analysis.source.tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showOrderModal && (
        <OrderDesignModal
          analysisId={analysis.id}
          referenceImageUrl={analysis.source?.imageUrls?.[0]}
          fields={persoFields}
          productTypeKey={analysis.productTypeKey}
          onClose={() => setShowOrderModal(false)}
        />
      )}
    </div>
  );
}

// Analiz detayinda fotografin yaninda eRank (CSV) metrikleri
function ErankPanel({ e }: { e: ErankListing }) {
  const money = (n: number) => "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const dt = (iso: string | null) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "-" : d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };
  const rows: Array<{ l: string; v: string; hi?: boolean }> = [
    { l: "Görüntülenme", v: e.totalViews.toLocaleString("tr-TR") },
    { l: "Günlük Gör.", v: e.dailyViews.toLocaleString("tr-TR", { maximumFractionDigits: 1 }) },
    { l: "Favori", v: e.favorites.toLocaleString("tr-TR") },
    { l: "Tah. Satış", v: e.estSales.toLocaleString("tr-TR"), hi: true },
    { l: "Tah. Ciro", v: money(e.estRevenue), hi: true },
    { l: "Fiyat", v: money(e.price) },
    { l: "Yaş (gün)", v: String(e.listingAge) },
  ];
  return (
    <div className="w-full flex-shrink-0 rounded-lg border border-gray-100 bg-gray-50/60 p-3 md:w-64">
      <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-700">
        <BarChart3 className="h-4 w-4 text-indigo-600" /> eRank Verisi
      </div>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.l} className="flex items-center justify-between text-xs">
            <span className="text-gray-500">{r.l}</span>
            <span className={`font-semibold tabular-nums ${r.hi ? "text-emerald-700" : "text-gray-800"}`}>{r.v}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 space-y-0.5 border-t border-gray-100 pt-2 text-[10px] text-gray-400">
        <div className="truncate" title={e.shopName}>Mağaza: {e.shopName}</div>
        {e.lastUpdated && <div>eRank güncel.: {e.lastUpdated}</div>}
        <div>Yüklendi: {dt(e.updatedAt)}</div>
      </div>
    </div>
  );
}

/** Aile hitabi varyantlari (Noel serisi 5. bolum): satan listedeki mama/grandma
 *  gibi kelimenin ailedeki diger halleri ayri arama kitleleri — yeni liste fikri. */
function FamilyVariantsPanel({ title, sourceTitle }: { title?: string | null; sourceTitle?: string | null }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const r = familyVariants(sourceTitle || title || "");
  if (!r.found) return null;
  return (
    <div className="rounded-lg border border-pink-100 bg-pink-50/50 p-3">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <span className="text-xs font-medium text-pink-800">Aile varyantları: “{r.found}” yerine {r.variants.length} hitapla yeni liste fikri</span>
        <span className="text-[11px] text-pink-600">{open ? "Gizle" : "Göster"}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-1">
          {r.grandkidsIdea && (
            <p className="text-[11px] text-pink-700">Çocuk isimli ürün: “Grandma + torun isimleri” kalıbı da satıyor (ör. Personalized Grandma Necklace with Grandchildren Names).</p>
          )}
          {r.variants.map((v) => (
            <div key={v.word} className="flex items-center gap-2 text-xs">
              <span className="w-20 shrink-0 text-[11px] text-gray-400">{v.group}</span>
              <span className="min-w-0 flex-1 truncate text-gray-700" title={v.title}>{v.title}</span>
              <button
                onClick={() => { void navigator.clipboard.writeText(v.title); setCopied(v.word); setTimeout(() => setCopied(null), 1200); }}
                className="shrink-0 text-[11px] text-indigo-500 hover:text-indigo-700"
              >
                {copied === v.word ? "Kopyalandı" : "Kopyala"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
